/*
 * Creator reels.
 *
 * Three jobs, all driven by one IntersectionObserver per card:
 *   1. nothing downloads until a card is near the viewport (data-src -> src)
 *   2. play while visible, pause when not — four looping videos off screen
 *      would burn battery for no reason
 *   3. sound is opt-in and exclusive: unmuting one mutes the rest
 *
 * Autoplay only works muted, so muted is the default and the sound button is
 * the only thing that changes it.
 */
(function () {

  class SnReel extends HTMLElement {
    connectedCallback() {
      this.video = this.querySelector('video');
      this.soundBtn = this.querySelector('[data-sound]');
      if (!this.video) return;

      this.video.muted = true;
      this.video.setAttribute('muted', '');
      this.video.playsInline = true;
      this.loaded = false;

      if (this.soundBtn) {
        this.soundBtn.addEventListener('click', this.toggleSound.bind(this));
      }
      // Tapping the frame is the gesture people expect from a reel.
      this.video.addEventListener('click', this.toggleSound.bind(this));

      if (!('IntersectionObserver' in window)) {
        this.load();
        return;
      }

      // Two thresholds: a generous one to fetch, a tighter one to play.
      this.fetchObserver = new IntersectionObserver(
        function (entries) {
          entries.forEach(
            function (e) {
              if (e.isIntersecting) {
                this.load();
                this.fetchObserver.disconnect();
              }
            }.bind(this)
          );
        }.bind(this),
        { rootMargin: '600px 0px' }
      );
      this.fetchObserver.observe(this);

      this.playObserver = new IntersectionObserver(
        function (entries) {
          entries.forEach(
            function (e) {
              if (e.isIntersecting) this.play();
              else this.pause();
            }.bind(this)
          );
        }.bind(this),
        { threshold: 0.35 }
      );
      this.playObserver.observe(this);

      // A backgrounded tab should not keep four videos decoding.
      this.onVisibility = function () {
        if (document.hidden) this.pause();
      }.bind(this);
      document.addEventListener('visibilitychange', this.onVisibility);
    }

    disconnectedCallback() {
      if (this.fetchObserver) this.fetchObserver.disconnect();
      if (this.playObserver) this.playObserver.disconnect();
      document.removeEventListener('visibilitychange', this.onVisibility);
    }

    load() {
      if (this.loaded) return;
      this.loaded = true;
      var v = this.video;
      // video_tag output already carries its sources; only the url route needs
      // promoting from data-src.
      var hd = v.getAttribute('data-src');
      var sd = v.getAttribute('data-src-sd');
      if (hd) {
        // Autoplay always starts on the 480p file. A rail card is roughly
        // 300px wide, so 270x480 is close to its display size, and four
        // muted 720p loops would pull ~28MB from someone who is only
        // scrolling past. The HD file is fetched in upgrade(), once
        // somebody actually engages with a reel.
        v.setAttribute('src', sd || hd);
        this.hd = hd;
        this.isHd = !sd;
      }
      v.preload = 'metadata';
      v.load();
      this.setAttribute('data-loaded', '');
    }

    /* Swap to the 720p file, keeping position and play state. */
    upgrade() {
      if (this.isHd || !this.hd) return;
      this.isHd = true;
      var v = this.video;
      var at = v.currentTime;
      var wasPlaying = !v.paused;
      v.setAttribute('src', this.hd);
      v.load();
      var resume = function () {
        v.removeEventListener('loadedmetadata', resume);
        try { v.currentTime = at; } catch (e) {}
        if (wasPlaying) {
          var r = v.play();
          if (r && r.catch) r.catch(function () {});
        }
      };
      v.addEventListener('loadedmetadata', resume);
      this.setAttribute('data-hd', '');
    }

    play() {
      if (!this.loaded) this.load();
      var r = this.video.play();
      if (r && r.catch) r.catch(function () {});
      this.setAttribute('data-playing', '');
    }

    pause() {
      if (!this.video) return;
      this.video.pause();
      this.removeAttribute('data-playing');
    }

    toggleSound(e) {
      if (e) e.preventDefault();
      var wantSound = this.video.muted;

      if (wantSound) {
        // Only one reel may be audible.
        document.querySelectorAll('sn-reel[data-sound-on]').forEach(function (other) {
          other.removeAttribute('data-sound-on');
          var ov = other.querySelector('video');
          if (ov) ov.muted = true;
          var ob = other.querySelector('[data-sound]');
          if (ob) ob.setAttribute('aria-pressed', 'false');
        });
      }

      this.video.muted = !wantSound;
      this.toggleAttribute('data-sound-on', wantSound);
      if (this.soundBtn) this.soundBtn.setAttribute('aria-pressed', String(wantSound));
      if (wantSound) {
        // Engaging with a reel is the signal that the HD file is worth its bytes.
        this.upgrade();
        this.play();
      }
    }
  }

  if (!customElements.get('sn-reel')) customElements.define('sn-reel', SnReel);
})();
